import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DeliveryStatus =
  | "pending"
  | "assigned"
  | "en_route"
  | "arriving"
  | "delivered"
  | "cancelled";

export type ExceptionType = "delay" | "unreachable" | "absent";

export interface DeliveryCourier {
  readonly id: string;
  readonly name: string;
  readonly phone: string | null;
}

export interface Delivery {
  readonly id: string;
  readonly ref: string; // e.g. YBK-0001
  readonly status: DeliveryStatus;
  readonly exception_type: ExceptionType | null;
  readonly exception_note: string | null;
  readonly recipient_name: string;
  readonly recipient_phone: string | null;
  readonly address: string;
  readonly eta: string | null; // ISO datetime
  readonly scheduled_at: string | null;
  readonly delivered_at: string | null;
  readonly payment_method: string | null;
  readonly amount_fcfa: number | null;
  readonly courier: DeliveryCourier | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface DeliveriesListResponse {
  readonly data: readonly Delivery[];
  readonly meta: {
    readonly total: number;
    readonly page: number;
    readonly per_page: number;
  };
}

export interface DeliveryDetailResponse {
  readonly delivery: Delivery;
}

export interface AssignDeliveryInput {
  readonly courier_id: string;
}

export interface ExceptionInput {
  readonly type: ExceptionType;
  readonly notes?: string;
}

export type RescheduleInput =
  | { readonly action: "reschedule"; readonly scheduled_at: string }
  | { readonly action: "return_to_depot" };

export interface LiveCourier {
  readonly driver_id: string;
  readonly name: string;
  readonly phone: string | null;
  readonly active_deliveries: number;
  readonly lat: number | null;
  readonly lng: number | null;
  readonly last_seen_at: string | null;
}

export interface LiveCouriersResponse {
  readonly couriers: readonly LiveCourier[];
}

export interface OtpGenerateInput {
  readonly delivery_id: string;
}

export interface OtpGenerateResponse {
  readonly sent: boolean;
  readonly expires_at: string;
}

export interface OtpVerifyInput {
  readonly delivery_id: string;
  readonly code: string;
}

export interface OtpVerifyResponse {
  readonly verified: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const BASE = "/api/admin/yoboulmako/dispatcher";

function buildListQs(params: {
  status?: string;
  exceptions?: boolean;
  date?: string;
  page?: number;
}): string {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.exceptions) qs.set("exceptions", "true");
  if (params.date) qs.set("date", params.date);
  if (params.page !== undefined && params.page > 1)
    qs.set("page", String(params.page));
  const str = qs.toString();
  return str ? `?${str}` : "";
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const dispatcherApi = {
  deliveries: {
    list(params: {
      status?: string;
      exceptions?: boolean;
      date?: string;
      page?: number;
    }): Promise<DeliveriesListResponse> {
      const qs = buildListQs(params);
      return adminFetch<DeliveriesListResponse>(
        `${BASE}/deliveries${qs}`,
      );
    },

    get(ref: string): Promise<DeliveryDetailResponse> {
      return adminFetch<DeliveryDetailResponse>(
        `${BASE}/deliveries/${encodeURIComponent(ref)}`,
      );
    },

    assign(
      id: string,
      body: AssignDeliveryInput,
    ): Promise<DeliveryDetailResponse> {
      return adminFetch<DeliveryDetailResponse>(`${BASE}/deliveries/${id}/assign`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },

    exception(
      id: string,
      body: ExceptionInput,
    ): Promise<DeliveryDetailResponse> {
      return adminFetch<DeliveryDetailResponse>(
        `${BASE}/deliveries/${id}/exception`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
    },

    reschedule(
      id: string,
      body: RescheduleInput,
    ): Promise<DeliveryDetailResponse> {
      return adminFetch<DeliveryDetailResponse>(
        `${BASE}/deliveries/${id}/reschedule`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
    },
  },

  couriers: {
    live(): Promise<LiveCouriersResponse> {
      return adminFetch<LiveCouriersResponse>(`${BASE}/couriers/live`);
    },
  },

  otp: {
    generate(input: OtpGenerateInput): Promise<OtpGenerateResponse> {
      return adminFetch<OtpGenerateResponse>(`${BASE}/otp/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
    },

    verify(input: OtpVerifyInput): Promise<OtpVerifyResponse> {
      return adminFetch<OtpVerifyResponse>(`${BASE}/otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
    },
  },
} as const;
